/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { TrashIcon } from '@coursera/cds-icons';

import { CMLOrHTML } from 'bundles/cml';
import type { OwnCommentsEdge } from 'bundles/grading/components/staff-graded-assignments/reusable-comments/api/types';
import {
  KEY_EVENT,
  commentToNaptimeCml,
} from 'bundles/grading/components/staff-graded-assignments/reusable-comments/utils';

import _t from 'i18n!nls/grading';

const styles = {
  listContainer: css`
    position: absolute;
    border: 1px solid var(--cds-color-blue-800);
    border-radius: 4px;
    padding: var(--cds-spacing-200);
    background-color: var(--cds-color-neutral-background-primary);

    /* +2px to match width of bordered CmlEditor */
    width: calc(100% + 2px);
    margin: 8px 0 0 -1px;
    z-index: 1;
  `,
  listHeading: css`
    color: var(--cds-color-grey-600);
    padding-left: var(--cds-spacing-100);
  `,
  list: css`
    list-style-type: none;
    padding: 0;
    margin: 0;
  `,
  item: css`
    cursor: pointer;
  `,
  itemFocused: css`
    background-color: var(--cds-color-blue-50);
  `,
  itemDivider: css`
    border-bottom: 1px solid var(--cds-color-grey-200);

    /* 4px margin to contain the line between rounded corners */
    margin: 0 4px;
  `,
  itemWrapper: css`
    display: flex;
    border-radius: 4px;

    &:hover {
      background-color: var(--cds-color-blue-50);
    }
  `,
  itemContent: css`
    padding: var(--cds-spacing-150) var(--cds-spacing-100) var(--cds-spacing-150) var(--cds-spacing-100);
    max-height: 150px;
    overflow: hidden;
    align-self: center;
    flex-grow: 1;

    .rc-CML > div {
      p:last-child {
        margin-bottom: 0;
      }
    }
  `,
  itemAction: css`
    align-self: center;
  `,
};

type Props = {
  items: OwnCommentsEdge[];
  allowDelete: boolean;
  showListHeading?: boolean;
  onItemSelect: (item: OwnCommentsEdge) => void;
  onItemDelete: (item: OwnCommentsEdge) => void;
};

/**
 * Renders a list of reusable comments. Takes care of keyboard navigation through items.
 * @param Props
 * @returns
 */
function ItemList({ items, allowDelete, showListHeading, onItemSelect, onItemDelete }: Props) {
  // focusedItemId to highlight an `li` when user tabs through the list
  const [focusedItemId, setFocusedItemId] = useState<string | null>(null);
  const ulRef = useRef<HTMLUListElement>(null);
  const suggestionsContainerRef = useRef<HTMLDivElement>(null);

  const handleKeyboardNavigation = (event: React.KeyboardEvent<HTMLDivElement | HTMLButtonElement>, index: number) => {
    let nextElement;
    if (event.nativeEvent.code === KEY_EVENT.ARROW_DOWN.key && index < items.length - 1) {
      nextElement = document.querySelector(`[data-itemid="${items[index + 1].node.id}"] > div >[role="button"]`);
    }
    if (event.nativeEvent.code === KEY_EVENT.ARROW_UP.key && index > 0) {
      nextElement = document.querySelector(`[data-itemid="${items[index - 1].node.id}"] > div > [role="button"]`);
    }
    if (nextElement) {
      (nextElement as HTMLElement).focus();
    }
  };

  const handleItemContentKeyDown = async (
    event: React.KeyboardEvent<HTMLDivElement>,
    item: OwnCommentsEdge,
    index: number
  ) => {
    if (event.nativeEvent.code === KEY_EVENT.SPACE.key || event.nativeEvent.code === KEY_EVENT.ENTER.key) {
      onItemSelect(item);
    } else {
      handleKeyboardNavigation(event, index);
    }
  };

  const handleItemDeleteKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    handleKeyboardNavigation(event, index);
  };

  return (
    <div css={styles.listContainer} ref={suggestionsContainerRef}>
      {showListHeading && (
        <Typography2 variant="subtitleMedium" component="span" css={styles.listHeading}>
          {_t('Your recent comments')}
        </Typography2>
      )}
      <ul css={styles.list} ref={ulRef} role="listbox">
        {items.map((item, i) => {
          return (
            <li css={[styles.item]} key={item.node.id} data-itemid={item.node.id} role="option" aria-selected={false}>
              <div css={[styles.itemWrapper, item.node.id === focusedItemId && styles.itemFocused]}>
                <div
                  css={styles.itemContent}
                  role="button"
                  onClick={() => onItemSelect(item)}
                  onKeyDown={(e) => handleItemContentKeyDown(e, item, i)}
                  onFocus={() => setFocusedItemId(item.node.id)}
                  tabIndex={0}
                  title={_t('Reuse this comment')}
                >
                  <CMLOrHTML value={commentToNaptimeCml(item.node.commentContent)} />
                </div>

                {allowDelete && (
                  <div css={styles.itemAction}>
                    <Button
                      onClick={() => onItemDelete(item)}
                      onFocus={() => setFocusedItemId(item.node.id)}
                      onKeyDown={(e) => handleItemDeleteKeyDown(e, i)}
                      variant="ghost"
                      size="small"
                      title={_t('Remove reuseable comment')}
                    >
                      <TrashIcon size="small" />
                    </Button>
                  </div>
                )}
              </div>
              <div css={[i !== items.length - 1 && styles.itemDivider]} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default ItemList;
